import { useEffect, useRef } from 'react'

/**
 * While `active`, calls `onMove` whenever the anchor's box moves or resizes,
 * checked once a frame, as Popover does. A dropdown portaled to the page
 * otherwise only hears about window scrolls and resizes, so it stays behind
 * when what holds its field moves on its own: a panel switching between side
 * panel and pop-up, a dialog settling into place, a row above it growing.
 */
export function useFollowAnchor(
  active: boolean,
  getAnchor: () => Element | null,
  onMove: () => void
) {
  const getAnchorRef = useRef(getAnchor)
  const onMoveRef = useRef(onMove)
  getAnchorRef.current = getAnchor
  onMoveRef.current = onMove

  useEffect(() => {
    if (!active || typeof window.requestAnimationFrame !== 'function') return
    let previous: DOMRect | null = getAnchorRef.current()?.getBoundingClientRect() ?? null
    let frame = 0
    const tick = () => {
      const rect = getAnchorRef.current()?.getBoundingClientRect() ?? null
      if (
        rect &&
        (!previous ||
          rect.top !== previous.top ||
          rect.left !== previous.left ||
          rect.width !== previous.width ||
          rect.height !== previous.height)
      ) {
        onMoveRef.current()
      }
      previous = rect
      frame = window.requestAnimationFrame(tick)
    }
    frame = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(frame)
  }, [active])
}
